import {
  faBuilding,
  faCalculatorSimple,
  faCreditCard,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { AccessorColumnDef, createColumnHelper } from "@tanstack/react-table";
import { capitalize } from "lodash-es";
import { formatDialect, hive, trino } from "sql-formatter";

import { Pill } from "src/base-components/Pill";
import { Cell, Header } from "src/base-components/Table";
import {
  ColumnMappingOutput,
  FeatureMappingOutput,
  FeatureQuery,
  FeatureQueryEnvironmentSetting,
  FeatureQueryStatus,
  FeatureResponse,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control";
import {
  EventTypeFilter,
  SortActionButton,
  StatusFilter,
} from "src/featureCatalogue/Features/FilterActions";
import {
  StatusDropdown,
  StatusToggleRequest,
} from "src/featureCatalogue/Features/StatusDropdown";
import { formatDuration as formatDurationUtil } from "src/utils/numbers";

export type Entity = "card" | "account" | "company";
export const ENTITY_ICON_MAP = {
  card: faCreditCard,
  account: faUser,
  company: faBuilding,
} as const;

export const EntityPills: React.FC<{ entities: string[] }> = ({ entities }) => (
  <>
    {entities.map((entity) => {
      const icon =
        ENTITY_ICON_MAP[entity as keyof typeof ENTITY_ICON_MAP] ?? faBuilding;
      return (
        <Pill key={entity} size="sm" variant="gray">
          <Pill.Icon icon={icon} />
          <Pill.Text>{capitalize(entity)}</Pill.Text>
        </Pill>
      );
    })}
  </>
);

export type Query = {
  id: string;
  environment_settings: Record<string, FeatureQueryEnvironmentSetting>;
  event: string;
  query: string;
  query_name?: string | null;
  backfilling_query: string;
  columns: ColumnMappingOutput;
  window_hop: number;
  window_length: number;
  created_at: string;
  updated_at: string;
  finalized_at?: string;
  etag: string;
  is_archived: boolean;
};

export type QueryRow = {
  id: string;
  column: string;
  window_hop: number;
  window_length: number;
  status: FeatureQueryStatus;
  entities: Entity[];
  event: string;
  query: string;
};

export type Feature = FeatureResponse;

export const mapFeatureQueryToQuery = (
  featureQuery: FeatureQuery | null | undefined,
): Query | undefined => {
  if (!featureQuery?.id) return undefined;
  return {
    id: featureQuery.id,
    etag: featureQuery.etag ?? "",
    event: featureQuery.event,
    query: featureQuery.query,
    query_name: featureQuery.query_name,
    columns: featureQuery.columns,
    window_hop: featureQuery.window_hop,
    window_length: featureQuery.window_length,
    environment_settings: featureQuery.environment_settings ?? {},
    created_at: featureQuery.created_at ?? "",
    updated_at: featureQuery.updated_at ?? "",
    finalized_at: featureQuery.finalized_at ?? undefined,
    is_archived: featureQuery.is_archived ?? false,
    backfilling_query: "",
  };
};

const DAY_IN_SECONDS = 24 * 60 * 60;
export const formatTimeWindow = (seconds: number): string => {
  if (seconds <= DAY_IN_SECONDS) {
    return formatDurationUtil(seconds * 1000, {
      locale: "long",
    });
  }

  return `${Math.floor(seconds / DAY_IN_SECONDS)} days`;
};

export const commonColumns = <T extends Feature | Query>() => {
  return [
    {
      accessorKey: "event",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            <span>Event</span>
            <EventTypeFilter />
          </div>
        </Header>
      ),
      cell: (info) => (
        <Cell info={info}>
          <Pill size="sm" variant="gray">
            <Pill.Icon icon={faCalculatorSimple} />
            <Pill.Text>{info.getValue() as string}</Pill.Text>
          </Pill>
        </Cell>
      ),
      minSize: 150,
      maxSize: 150,
      enableSorting: true,
      filterFn: "equals",
    },
    {
      accessorKey: "window_length",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            <span className="whitespace-nowrap">Time window</span>
            <SortActionButton column={info.column} />
          </div>
        </Header>
      ),
      cell: (info) => (
        <Cell info={info}>{formatTimeWindow(info.getValue() as number)}</Cell>
      ),
      minSize: 130,
      maxSize: 130,
      filterFn: "equals",
      enableSorting: true,
    },
    {
      accessorKey: "window_hop",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            <span>Freshness</span>
            <SortActionButton column={info.column} />
          </div>
        </Header>
      ),
      cell: (info) => (
        <Cell info={info}>{formatTimeWindow(info.getValue() as number)}</Cell>
      ),
      minSize: 130,
      maxSize: 130,
      filterFn: "equals",
      enableSorting: true,
    },
  ] as const satisfies AccessorColumnDef<T>[];
};

export const formatAthenaSqlQuery = (sql: string | undefined) => {
  if (!sql) return "";

  try {
    return formatDialect(sql, { dialect: trino });
  } catch {
    try {
      return formatDialect(sql, { dialect: hive });
    } catch {
      return sql;
    }
  }
};

export type OutletContext = {
  type: "header" | "content";
  searchQuery?: string;
};

export const getQueryEditPermissions = (
  query: Pick<Query, "environment_settings"> | undefined,
  capabilities: { canEditLiveQuery: boolean; canEditSandboxQuery: boolean },
): { readOnly: boolean; readOnlyReason: string } => {
  const isLiveActive =
    query?.environment_settings?.live?.status === FeatureQueryStatus.ACTIVE;

  const readOnly = isLiveActive
    ? !capabilities.canEditLiveQuery
    : !capabilities.canEditSandboxQuery;

  const readOnlyReason = isLiveActive
    ? "You cannot edit a Query which is currently active on Live"
    : "You don't have permission to edit this query";

  return { readOnly, readOnlyReason };
};

export const isActiveQuery = (
  query: Query,
  environment: PreviewQueryRequestEnvironmentEnum,
) => {
  return (
    query.environment_settings[environment].status === FeatureQueryStatus.ACTIVE
  );
};

type StatusColumnsConfig<T> = {
  getEnvironmentSettings: (
    row: T,
  ) => Record<string, FeatureQueryEnvironmentSetting> | undefined;
  hasQuery: (row: T) => boolean;
  getQueryId: (row: T) => string;
  getEtag: (row: T) => string;
  getEntities: (row: T) => string[];
  getEvent: (row: T) => string;
  getFeatures: (row: T) => FeatureMappingOutput[];
  onRequestStatusChange: (request: StatusToggleRequest) => void;
};

export const statusColumns = <T extends Feature | Query>(
  columnHelper: ReturnType<typeof createColumnHelper<T>>,
  config: StatusColumnsConfig<T>,
) => {
  const {
    getEnvironmentSettings,
    hasQuery,
    getQueryId,
    getEtag,
    getEntities,
    getEvent,
    getFeatures,
    onRequestStatusChange,
  } = config;

  const createColumn = (environment: "sandbox" | "live") =>
    columnHelper.accessor(
      ((row: T) =>
        getEnvironmentSettings(row)?.[environment]?.status) as Parameters<
        typeof columnHelper.accessor
      >[0],
      {
        id: `${environment}_status`,
        header: (info) => (
          <Header info={info}>
            <div className="flex items-center gap-x-2">
              <span className="whitespace-nowrap">
                {capitalize(environment)} status
              </span>
              <StatusFilter property={`${environment}_status`} />
            </div>
          </Header>
        ),
        cell: (info) => {
          const currentStatus = info.getValue() as
            | FeatureQueryStatus
            | undefined;
          const row = info.row.original;

          return currentStatus ? (
            <Cell info={info}>
              <div
                className="flex w-full justify-between"
                onClick={(e) => e.stopPropagation()}
              >
                <StatusDropdown
                  disabled={!hasQuery(row)}
                  entities={getEntities(row)}
                  environment={
                    environment === "sandbox"
                      ? PreviewQueryRequestEnvironmentEnum.SANDBOX
                      : PreviewQueryRequestEnvironmentEnum.LIVE
                  }
                  etag={getEtag(row)}
                  event={getEvent(row)}
                  features={getFeatures(row)}
                  queryId={getQueryId(row)}
                  value={currentStatus}
                  onRequestToggle={onRequestStatusChange}
                />
              </div>
            </Cell>
          ) : null;
        },
        enableGlobalFilter: false,
        filterFn: "equals",
        enableSorting: true,
      },
    );

  return [createColumn("sandbox"), createColumn("live")] as const;
};

export type FilterConfig<T> = {
  entityFilterKey: string;
  getEntityValues: (item: T) => string[];
  getEvent: (item: T) => string;
  getSandboxStatus: (item: T) => string | undefined;
  getLiveStatus: (item: T) => string | undefined;
};

export type FilterOptions = { entityTypes: string[]; eventTypes: string[] };
